import React, { useMemo, useState } from "react";
import {
  TextField,
  Typography,
  Autocomplete,
  CircularProgress,
  IconButton,
  Box,
  Link,
} from "@mui/material";
import MessageDialog from "../MessageForm/MessageDialog";
import { Close, Search } from "@mui/icons-material";
import {
  useLazyGetAutoCompleteResultQuery,
} from "../../Services/auto-complete.services";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";

// Below this many characters a miss is just "still typing", not "not found".
const NO_MATCH_MIN_CHARS = 4;
// realestateapi.com returns at most this many suggestions.
const MAX_SUGGESTIONS = 10;

/**
 * A big building's list stops at the first 10 units (8400 De Longpre showed
 * 101–203, so the owner of 302 couldn't see theirs, Oct 20 call). Searching
 * with the unit finds it; this says so, at the top of the list.
 */
export const unitHintFor = (results: any[]): string | null => {
  if (!Array.isArray(results) || results.length < MAX_SUGGESTIONS) return null;
  const units = results.filter((r) => r?.unit);
  if (units.length < 5 || new Set(units.map((r) => `${r.house}|${r.street}`)).size !== 1) return null;
  const example = `${units[0].house} ${String(units[0].street || "").trim()} 302`;
  return `Showing the first ${MAX_SUGGESTIONS} units. Add your unit number to find yours, like “${example}”.`;
};

const SearchDropdown = () => {
  const [searchQuery, setSearchQuery]: any = useState("");
  const [options, setOptions]: any = useState([]);
  const [loading, setLoading] = useState(false);
  // The query the current `options` answer, so "no matches" only shows for a
  // search that actually finished.
  const [answered, setAnswered] = useState("");
  const [askOpen, setAskOpen] = useState(false);
  const navigate = useNavigate();

  const [trigger, { isLoading }]: any = useLazyGetAutoCompleteResultQuery({});

  const debouncedSearch = useMemo(
    () =>
      debounce(async (value) => {
        try {
          setLoading(true);
          const { data = { results: [] } } = await trigger(value).unwrap();
          const results = data?.results || [];
          const hint = unitHintFor(results);
          // First, not last: the list scrolls, and the 11th row was out of sight.
          setOptions(hint ? [{ kind: "unit-hint", title: hint }, ...results] : results);
        } catch (error) {
          // Request failed (network/backend down, rate limit, etc.) — show no
          // results instead of leaving an uncaught promise rejection that
          // crashes the dev error overlay.
          setOptions([]);
        } finally {
          setLoading(false);
          setAnswered(value);
        }
      }, 300),
    [trigger]
  );

  const query = String(searchQuery || "").trim();
  const noMatch =
    !loading &&
    query.length >= NO_MATCH_MIN_CHARS &&
    answered === searchQuery &&
    (options || []).length === 0;

  const handleInputChange = (event: any, value: any) => {
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleClick = (data?: any) => {
    if (data?.kind === "unit-hint") return; // information, not a place
    const {
      searchType,
      zip,
      id,
      city,
      county,
      neighborhoodId,
      state,
    } = data;

    if (id) {
      navigate(`/${id}/property-id/${id}`.toString());
      return;
    }
    if ((searchType === "C" && city) || (searchType === "S" && city)) {
      navigate(`/${city}/city/${city}`);
      return;
    }
    if (state && searchType === "T") {
      navigate(`/${state}/state/${state}`);
      return;
    }
    if (neighborhoodId && searchType === "G") {
      navigate(`${neighborhoodId}/neighborhood_id/${neighborhoodId}`);
      return;
    }
    if (zip && searchType === "Z") {
      navigate(`/${zip}/zip/${zip}`);
      return;
    }

    if (searchType === "N" && county) {
      navigate(`/${county}/county/${county}`);
      return;
    }
  };

  return (
    <Box>
      <Autocomplete
        fullWidth
        freeSolo
        open={true}
        options={options || []}
        multiple={true}
        getOptionLabel={(option?: any) => option.title}
        inputValue={searchQuery || ""}
        onInputChange={handleInputChange}
        filterOptions={(options) => options || []}
        loading={loading}
        sx={{
          borderRadius: "10px",
          border: "none",
          ".MuiInputBase-root": {
            borderRadius: "10px",
            p: {
              xs: "7px 20px",
              md: "9px 20px",
            },
          },
          fieldset: {
            borderColor: "primary.main",
          },
        }}
        clearIcon={
          isLoading ? (
            <CircularProgress color="success" size={20} />
          ) : (
            <IconButton
              onClick={() => {
                setOptions([]);
                setSearchQuery("");
              }}
            >
              <Close style={{ color: "red" }} />
            </IconButton>
          )
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            fullWidth
            placeholder="Find your property"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <Search
                  sx={{
                    fontSize: {
                      xs: 25,
                      md: 40,
                    },
                    mr: 2,
                  }}
                />
              ),
            }}
            sx={{
              backgroundColor: "white",
              borderRadius: "10px",
              border: "none",
              "& .MuiOutlinedInput-root": {
                "& input::placeholder": {
                  color: "#666", // ✅ Placeholder text color
                  fontSize: "1.1rem", // ✅ Placeholder font size
                  fontWeight: 500, // ✅ Placeholder font weight
                  opacity: 1, // Make sure it's not too light (browser default)
                },
              },
              ".MuiContainer-root": {
                p: "0px",
              },
            }}
          />
        )}
        getOptionDisabled={(option: any) => option?.kind === "unit-hint"}
        renderOption={(props, option) =>
          option?.kind === "unit-hint" ? (
            <li
              {...props}
              key="unit-hint"
              data-testid="search-unit-hint"
              style={{ ...(props as any).style, opacity: 1, cursor: "default", whiteSpace: "normal" }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                {option.title}
              </Typography>
            </li>
          ) : (
            <li {...props} key={option.name} onClick={() => handleClick(option)}>
              <Typography variant="body1">{option.title}</Typography>
            </li>
          )
        }
        noOptionsText="No results found"
      />
      {/* freeSolo never shows noOptionsText, so a miss used to show nothing. */}
      {noMatch && (
        <Box
          role="status"
          data-testid="search-no-match"
          sx={{
            mt: 1,
            px: 2,
            py: 1.5,
            backgroundColor: "white",
            borderRadius: "10px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            textAlign: "left",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No matches for “{query}”. Check the spelling, or{" "}
            <Link component="button" type="button" onClick={() => setAskOpen(true)} sx={{ fontWeight: 600, verticalAlign: "baseline" }}>
              tell us the address
            </Link>{" "}
            and we'll find it for you.
          </Typography>
        </Box>
      )}
      <MessageDialog
        open={askOpen}
        onClose={() => setAskOpen(false)}
        title="Can't find your home?"
        intro="Send us the address and we'll find it or add it for you."
        topic="find-home"
        messageLabel="Address and anything else we should know"
        defaultMessage={query}
        defaultSubject={`Searched for: ${query}`.slice(0, 200)}
      />
    </Box>
  );
};

export default SearchDropdown;
